import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Settings = () => {
  const { tenant } = useAuth();
  const [uploadFile, setUploadFile] = React.useState(null);
  const [uploadLoading, setUploadLoading] = React.useState(false);
  const [uploadResult, setUploadResult] = React.useState(null);
  const [deleteTermLoading, setDeleteTermLoading] = React.useState(false);
  const [deleteTermResult, setDeleteTermResult] = React.useState(null);
  const handleDeleteTerminated = async () => {
    if(!window.confirm('Delete all Terminated users from BMS database?')) return;
    setDeleteTermLoading(true);
    setDeleteTermResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/crm/delete-terminated', {
        method:'DELETE',
        headers:{'Authorization':'Bearer '+token}
      });
      const data = await res.json();
      setDeleteTermResult(data);
    } catch(e) {
      setDeleteTermResult({success:false, message:e.message});
    }
    setDeleteTermLoading(false);
  };

  const handleCustomerUpload = async () => {
    if (!uploadFile) { alert('Please select an Excel file'); return; }
    setUploadLoading(true);
    setUploadResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await fetch('/api/crm/upload-customers', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      });
      const data = await res.json();
      if (data.jobId) {
        // Poll for status
        setUploadResult({ success: true, message: '⏳ Processing in background...' });
        localStorage.setItem('bms_upload_job', data.jobId);
        const poll = setInterval(async () => {
          try {
            const sr = await fetch('/api/crm/upload-status/' + data.jobId, {
              headers: { 'Authorization': 'Bearer ' + token }
            });
            const sd = await sr.json();
            if (sd.status === 'done') {
              clearInterval(poll);
              localStorage.removeItem('bms_upload_job');
              setUploadResult(sd);
              setUploadLoading(false);
            } else if (sd.status === 'error') {
              clearInterval(poll);
              setUploadResult({ success: false, message: sd.message });
              setUploadLoading(false);
            } else if (sd.status === 'processing') {
              setUploadResult({ success: true, message: '⏳ ' + (sd.progress||0) + '% | ' + (sd.processed||0) + '/' + (sd.total||0) + ' records | ✅ ' + (sd.inserted||0) + ' new | 🔄 ' + (sd.updated||0) + ' updated' });
            }
          } catch(e) {}
        }, 5000);
      } else {
        setUploadResult(data);
        setUploadLoading(false);
      }
    } catch(e) {
      setUploadResult({ success: false, message: e.message });
      setUploadLoading(false);
    }
  };

  // Resume upload polling if browser was closed during upload
  React.useEffect(() => {
    const savedJob = localStorage.getItem('bms_upload_job');
    if(savedJob) {
      setUploadLoading(true);
      setUploadResult({ success: true, message: '⏳ Upload in progress...' });
      const token = localStorage.getItem('token');
      const poll = setInterval(async () => {
        try {
          const sr = await fetch('/api/crm/upload-status/' + savedJob, {
            headers: { 'Authorization': 'Bearer ' + token }
          });
          const sd = await sr.json();
          if (sd.status === 'done') {
            clearInterval(poll);
            localStorage.removeItem('bms_upload_job');
            setUploadResult(sd);
            setUploadLoading(false);
          } else if (sd.status === 'error') {
            clearInterval(poll);
            localStorage.removeItem('bms_upload_job');
            setUploadResult({ success: false, message: sd.message });
            setUploadLoading(false);
          } else if (sd.status === 'processing') {
            setUploadResult({ success: true, message: '⏳ ' + (sd.progress||0) + '% | ' + (sd.processed||0) + '/' + (sd.total||0) + ' records | ✅ ' + (sd.inserted||0) + ' new | 🔄 ' + (sd.updated||0) + ' updated' });
          } else if (sd.status === 'not_found') {
            clearInterval(poll);
            localStorage.removeItem('bms_upload_job');
            setUploadLoading(false);
          }
        } catch(e) {}
      }, 5000);
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [showTestForm, setShowTestForm] = useState(false);
  const [subDistList, setSubDistList] = useState([]);
  const [subDistForm, setSubDistForm] = useState({ sub_dist_code: '', sub_dist_name: '', support_phone: '', support_name: '' });
  const [subDistLoading, setSubDistLoading] = useState(false);
  const [editingSubDist, setEditingSubDist] = useState(null);
  const [form, setForm] = useState({
    app_name: '',
    primary_color: '#1B6B3A',
    secondary_color: '#2E8B57',
    ticket_prefix: '',
    level1_hours: 24,
    level2_hours: 24,
    level3_hours: 24,
    level4_hours: 24,
    warning_before_hours: 4,
    whatsapp_enabled: false,
    whatsapp_phone_number_id: '',
    whatsapp_api_key: '',
    whatsapp_sender_number: '',
    whatsapp_sender_name: ''
  });

  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';
  const [categoryHours, setCategoryHours] = useState([]);

  useEffect(() => {
    if (tenant) {
      setLogoUrl(tenant.branding.logo_url || '');
      setForm({
        app_name: tenant.branding.app_name || '',
        primary_color: tenant.branding.primary_color || '#1B6B3A',
        secondary_color: tenant.branding.secondary_color || '#2E8B57',
        ticket_prefix: tenant.ticket_prefix || '',
        level1_hours: tenant.sla?.level1_hours || 24,
        level2_hours: tenant.sla?.level2_hours || 24,
        level3_hours: tenant.sla?.level3_hours || 24,
        level4_hours: tenant.sla?.level4_hours || 24,
        warning_before_hours: tenant.sla?.warning_before_hours || 4,
        whatsapp_enabled: tenant.whatsapp ? tenant.whatsapp.whatsapp_enabled || false : false,
        whatsapp_phone_number_id: tenant.whatsapp ? tenant.whatsapp.phone_number_id || '' : '',
        whatsapp_api_key: tenant.whatsapp ? tenant.whatsapp.api_key || '' : '',
        whatsapp_sender_number: tenant.whatsapp ? tenant.whatsapp.sender_number || '' : '',
        whatsapp_sender_name: tenant.whatsapp ? tenant.whatsapp.sender_name || '' : ''
      });
      // Load category SLA hours
      if(tenant.complaint_categories && tenant.complaint_categories.length){
        setCategoryHours(tenant.complaint_categories.filter(cc=>cc.is_active).map(cc=>({ name: cc.name_en, hours: cc.expected_hours||24, id: cc._id })));
      }
    }
  }, [tenant]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    setLogoLoading(true);
    setError('');
    try {
      const res = await api.post('/tenants/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(res.data.logo_url);
      setSuccess('Logo uploaded successfully!');
    } catch (err) {
      setError('Failed to upload logo');
    } finally {
      setLogoLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      await api.put('/tenants/settings', {
        branding: { app_name: form.app_name, primary_color: form.primary_color, secondary_color: form.secondary_color },
        ticket_prefix: form.ticket_prefix,
        sla: { level1_hours: Number(form.level1_hours), level2_hours: Number(form.level2_hours), level3_hours: Number(form.level3_hours), level4_hours: Number(form.level4_hours), warning_before_hours: Number(form.warning_before_hours) },
        complaint_categories: tenant?.complaint_categories?.map(cc=>{ const ch=categoryHours.find(h=>h.id===String(cc._id)); return {...cc, expected_hours: ch?Number(ch.hours):cc.expected_hours||24}; }),
        whatsapp: {
          whatsapp_enabled: form.whatsapp_enabled,
          phone_number_id: form.whatsapp_phone_number_id,
          api_key: form.whatsapp_api_key,
          sender_number: form.whatsapp_sender_number,
          sender_name: form.whatsapp_sender_name
        }
      });
      setSuccess('Settings saved successfully! Refresh page to see changes.');
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhone) return setError('Please enter a phone number to test');
    setTestLoading(true);
    setError('');
    try {
      await api.post('/tenants/whatsapp/test', { phone: testPhone });
      setSuccess('Test WhatsApp message sent to ' + testPhone + '! Check your phone.');
      setShowTestForm(false);
      setTestPhone('');
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to send test message. Check your WhatsApp credentials!');
    } finally {
      setTestLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#333', fontSize: '14px', fontWeight: '500' };
  useEffect(() => { fetchSubDist(); }, []);

  const fetchSubDist = async () => {
    try {
      const r = await fetch('/api/subdist-support', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
      const d = await r.json();
      if (d.success) setSubDistList(d.data);
    } catch(e) {}
  };

  const handleSubDistSave = async () => {
    if (!subDistForm.sub_dist_code || !subDistForm.sub_dist_name || !subDistForm.support_phone)
      return setError('Sub-dist code, name and support phone are required');
    setSubDistLoading(true);
    try {
      const url = editingSubDist ? '/api/subdist-support/' + editingSubDist : '/api/subdist-support';
      const method = editingSubDist ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify(subDistForm)
      });
      const d = await r.json();
      if (d.success) {
        setSuccess(editingSubDist ? 'Updated!' : 'Added!');
        setSubDistForm({ sub_dist_code: '', sub_dist_name: '', support_phone: '', support_name: '' });
        setEditingSubDist(null);
        fetchSubDist();
      } else setError(d.message);
    } catch(e) { setError('Error saving'); }
    setSubDistLoading(false);
  };

  const handleSubDistDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await fetch('/api/subdist-support/' + id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
      fetchSubDist();
    } catch(e) {}
  };

  const handleSubDistEdit = (item) => {
    setEditingSubDist(item._id);
    setSubDistForm({ sub_dist_code: item.sub_dist_code, sub_dist_name: item.sub_dist_name, support_phone: item.support_phone, support_name: item.support_name || '' });
  };

  const sectionStyle = { background: 'white', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' };
  const headingStyle = { margin: '0 0 20px', color: primaryColor, borderBottom: '2px solid #f0f0f0', paddingBottom: '10px' };

  return (
    <div style={{ maxWidth: '800px' }}>
      <h2 style={{ margin: '0 0 20px', color: '#333' }}>White Label Settings</h2>
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontWeight: '500' }}>{success}</div>}
      {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>}
      <form onSubmit={handleSave}>
        <div style={sectionStyle}>
          <h3 style={headingStyle}>Branding</h3>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Company Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {logoUrl ? (
                <img src={'https://desk.fibralink.co.in' + logoUrl} alt="Logo" style={{ width: '80px', height: '80px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '8px', padding: '8px', background: 'white' }} />
              ) : (
                <div style={{ width: '80px', height: '80px', border: '2px dashed #ddd', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '11px', textAlign: 'center', background: 'white' }}>No Logo</div>
              )}
              <div>
                <input type="file" id="logoUpload" accept="image/png,image/jpeg,image/jpg" onChange={handleLogoUpload} style={{ display: 'none' }} />
                <label htmlFor="logoUpload" style={{ display: 'inline-block', padding: '10px 20px', background: logoLoading ? '#ccc' : primaryColor, color: 'white', borderRadius: '6px', cursor: logoLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  {logoLoading ? 'Uploading...' : 'Upload Logo'}
                </label>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>PNG or JPG. Max 2MB. Recommended: 200x200px</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>App / Portal Name</label>
              <input type="text" placeholder="e.g. MyISP Support" value={form.app_name} onChange={e => setForm({...form, app_name: e.target.value})} style={inputStyle} />
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>Shows on portal and sidebar</p>
            </div>
            <div>
              <label style={labelStyle}>Ticket ID Prefix</label>
              <input type="text" placeholder="e.g. ABC" value={form.ticket_prefix} onChange={e => setForm({...form, ticket_prefix: e.target.value.toUpperCase()})} maxLength={5} style={inputStyle} />
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>Tickets: {form.ticket_prefix || 'ABC'}-2026-00001</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Primary Color</label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} style={{ width: '50px', height: '40px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} />
                <input type="text" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} style={{...inputStyle, flex: 1}} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Secondary Color</label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input type="color" value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})} style={{ width: '50px', height: '40px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} />
                <input type="text" value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})} style={{...inputStyle, flex: 1}} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '16px', padding: '16px', borderRadius: '8px', background: form.primary_color, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>{form.app_name || 'App Name Preview'}</span>
            <span style={{ color: 'white', fontSize: '13px', background: form.secondary_color, padding: '6px 12px', borderRadius: '6px' }}>Preview</span>
          </div>
        </div>
        <div style={sectionStyle}>
          <h3 style={headingStyle}>SLA Settings (Hours)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div><label style={labelStyle}>Level 1 (Sub Dist)</label><input type="number" min="1" max="72" value={form.level1_hours} onChange={e => setForm({...form, level1_hours: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Level 2 (Distribution)</label><input type="number" min="1" max="72" value={form.level2_hours} onChange={e => setForm({...form, level2_hours: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Level 3 (Dist Head)</label><input type="number" min="1" max="72" value={form.level3_hours} onChange={e => setForm({...form, level3_hours: e.target.value})} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label style={labelStyle}>Level 4 (Director)</label><input type="number" min="1" max="72" value={form.level4_hours} onChange={e => setForm({...form, level4_hours: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Warning Before (Hours)</label><input type="number" min="1" max="12" value={form.warning_before_hours} onChange={e => setForm({...form, warning_before_hours: e.target.value})} style={inputStyle} /></div>
          </div>
        </div>
        <div style={sectionStyle}>
          <h3 style={headingStyle}>Category SLA Targets (Hours)</h3>
          <p style={{ fontSize:'12px', color:'#64748b', marginBottom:'12px' }}>Set expected resolution time for each ticket category — used for tech KRA scoring</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            {categoryHours.map((cat,i)=>(
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f8fafc', borderRadius:'8px', padding:'10px 14px' }}>
                <label style={{ fontSize:'13px', fontWeight:'600', color:'#334155' }}>{cat.name}</label>
                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                  <input type="number" min="1" max="168" value={cat.hours}
                    onChange={e=>setCategoryHours(prev=>prev.map((c,ci)=>ci===i?{...c,hours:e.target.value}:c))}
                    style={{ width:"60px", padding:"4px 8px", border:"1px solid #ddd", borderRadius:"6px", fontSize:"13px", textAlign:"center" }} />
                  <span style={{ fontSize:"11px", color:"#64748b" }}>hrs</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={sectionStyle}>
          <h3 style={headingStyle}>WhatsApp Configuration</h3>
          <div style={{ marginBottom: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '8px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#1565c0', fontWeight: '600' }}>📱 Meta WhatsApp Cloud API Setup</p>
            <p style={{ margin: '0', fontSize: '12px', color: '#1565c0' }}>Get credentials from: developers.facebook.com → Your App → WhatsApp → API Setup</p>
          </div>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.whatsapp_enabled} onChange={e => setForm({...form, whatsapp_enabled: e.target.checked})} style={{ width: '18px', height: '18px' }} />
              <span style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>Enable WhatsApp Notifications</span>
            </label>
          </div>
          {form.whatsapp_enabled && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Phone Number ID *</label>
                  <input type="text" placeholder="e.g. 123456789012345" value={form.whatsapp_phone_number_id} onChange={e => setForm({...form, whatsapp_phone_number_id: e.target.value})} style={inputStyle} />
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>From Meta Developer Console</p>
                </div>
                <div>
                  <label style={labelStyle}>Access Token *</label>
                  <input type="password" placeholder="Your permanent access token" value={form.whatsapp_api_key} onChange={e => setForm({...form, whatsapp_api_key: e.target.value})} style={inputStyle} />
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>From Meta Developer Console</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>WhatsApp Number</label>
                  <input type="text" placeholder="e.g. 919876543210" value={form.whatsapp_sender_number} onChange={e => setForm({...form, whatsapp_sender_number: e.target.value})} style={inputStyle} />
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>Your registered WhatsApp number</p>
                </div>
                <div>
                  <label style={labelStyle}>Sender Name</label>
                  <input type="text" placeholder="e.g. PDIC Support" value={form.whatsapp_sender_name} onChange={e => setForm({...form, whatsapp_sender_name: e.target.value})} style={inputStyle} />
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>Name shown on WhatsApp</p>
                </div>
              </div>
              <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px', color: '#333' }}>Message Templates Required</h4>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#666' }}>Create these templates in Meta Business Manager:</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {['ticket_created', 'ticket_resolved', 'ticket_escalated', 'sla_warning'].map(t => (
                    <div key={t} style={{ background: 'white', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px', fontWeight: '600', color: '#333' }}>✅ {t}</div>
                  ))}
                </div>
              </div>
              <div style={{ background: '#f0fff4', padding: '16px', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 12px', color: '#1B6B3A' }}>Test WhatsApp</h4>
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>Send a test message to verify your setup</p>
                {showTestForm ? (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input type="text" placeholder="Enter phone number (e.g. 9876543210)" value={testPhone} onChange={e => setTestPhone(e.target.value)} style={{...inputStyle, flex: 1}} />
                    <button type="button" onClick={handleTestWhatsApp} disabled={testLoading} style={{ padding: '10px 20px', background: testLoading ? '#ccc' : '#1B6B3A', color: 'white', border: 'none', borderRadius: '6px', cursor: testLoading ? 'not-allowed' : 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {testLoading ? 'Sending...' : 'Send Test'}
                    </button>
                    <button type="button" onClick={() => setShowTestForm(false)} style={{ padding: '10px 16px', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowTestForm(true)} style={{ padding: '10px 20px', background: '#1B6B3A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>📱 Send Test Message</button>
                )}
              </div>
            </div>
          )}
        </div>
        <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: loading ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </form>

      {/* Sub-Dist Support Numbers */}
      <div style={{ ...sectionStyle, marginTop: '20px' }}>
        <h3 style={{ margin: '0 0 6px', color: '#1B6B3A' }}>📞 WhatsApp Bot - Support Numbers</h3>
        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#666' }}>Manage sub-distributor support phone numbers shown to customers via WhatsApp bot.</p>

        {/* Add/Edit Form */}
        <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px', color: '#333' }}>{editingSubDist ? '✏️ Edit Entry' : '➕ Add New'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input placeholder="Sub-dist Code (e.g. KB13)" value={subDistForm.sub_dist_code} onChange={e => setSubDistForm({...subDistForm, sub_dist_code: e.target.value.toUpperCase()})} style={{...inputStyle}} />
            <input placeholder="Sub-dist Name" value={subDistForm.sub_dist_name} onChange={e => setSubDistForm({...subDistForm, sub_dist_name: e.target.value})} style={{...inputStyle}} />
            <input placeholder="Support Phone (e.g. 9400000000)" value={subDistForm.support_phone} onChange={e => setSubDistForm({...subDistForm, support_phone: e.target.value})} style={{...inputStyle}} />
            <input placeholder="Contact Person Name (optional)" value={subDistForm.support_name} onChange={e => setSubDistForm({...subDistForm, support_name: e.target.value})} style={{...inputStyle}} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubDistSave} disabled={subDistLoading} style={{ padding: '10px 20px', background: '#1B6B3A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
              {subDistLoading ? 'Saving...' : editingSubDist ? 'Update' : 'Add'}
            </button>
            {editingSubDist && <button onClick={() => { setEditingSubDist(null); setSubDistForm({ sub_dist_code: '', sub_dist_name: '', support_phone: '', support_name: '' }); }} style={{ padding: '10px 16px', background: 'white', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>}
          </div>
        </div>

        {/* List */}
        {subDistList.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>No support numbers added yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f0fff4' }}>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', color: '#1B6B3A', borderBottom: '2px solid #e0e0e0' }}>Code</th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', color: '#1B6B3A', borderBottom: '2px solid #e0e0e0' }}>Sub-Distributor</th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', color: '#1B6B3A', borderBottom: '2px solid #e0e0e0' }}>Support Number</th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', color: '#1B6B3A', borderBottom: '2px solid #e0e0e0' }}>Contact Person</th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', color: '#1B6B3A', borderBottom: '2px solid #e0e0e0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subDistList.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600', color: '#1B6B3A' }}>{item.sub_dist_code}</td>
                  <td style={{ padding: '10px', fontSize: '13px' }}>{item.sub_dist_name}</td>
                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: '600' }}>📞 {item.support_phone}</td>
                  <td style={{ padding: '10px', fontSize: '13px', color: '#666' }}>{item.support_name || '-'}</td>
                  <td style={{ padding: '10px' }}>
                    <button onClick={() => handleSubDistEdit(item)} style={{ marginRight: '6px', padding: '5px 10px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✏️ Edit</button>
                    <button onClick={() => handleSubDistDelete(item._id)} style={{ padding: '5px 10px', background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#dc3545' }}>🗑️ Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Customer Data Upload */}
      <div style={{ background:'white', borderRadius:'10px', padding:'24px', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', marginBottom:'20px' }}>
        <div style={{ fontSize:'16px', fontWeight:'700', marginBottom:'16px', color:'#1e293b' }}>📤 Customer Data Upload</div>
        <div style={{ fontSize:'13px', color:'#64748b', marginBottom:'16px' }}>
          Upload BMS Excel export to update customer master. Permanently left customers are skipped automatically.
        </div>
        <div style={{ display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
          <input type="file" accept=".xlsx,.xls,.csv,.zip" onChange={e=>setUploadFile(e.target.files[0])}
            style={{ padding:'8px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'13px', cursor:'pointer' }} />
          <button onClick={handleCustomerUpload} disabled={uploadLoading || !uploadFile}
            style={{ padding:'10px 24px', background:'#1B6B3A', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'13px', opacity: uploadLoading ? 0.7 : 1 }}>
            {uploadLoading ? '⏳ Uploading...' : '📤 Upload & Sync'}
          </button>
          <button onClick={handleDeleteTerminated} disabled={deleteTermLoading}
            style={{ padding:'10px 24px', background:'#dc2626', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'13px', opacity: deleteTermLoading ? 0.7 : 1 }}>
            {deleteTermLoading ? '⏳ Deleting...' : '🗑️ Delete Terminated'}
          </button>
        </div>
        {uploadResult && (
          <div style={{ marginTop:'16px', padding:'14px', borderRadius:'8px', background: uploadResult.success ? '#f0fdf4' : '#fff0f0', border: '1px solid ' + (uploadResult.success ? '#86efac' : '#fca5a5') }}>
            {uploadResult.success ? (
              <div>
                <div style={{ fontWeight:'700', color:'#166534', marginBottom:'8px' }}>✅ {uploadResult.message}</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginTop:'10px' }}>
                  {[
                    {label:'Total Rows', value:uploadResult.total, color:'#1B6B3A'},
                    {label:'New Added', value:uploadResult.inserted, color:'#2563eb'},
                    {label:'Updated', value:uploadResult.updated, color:'#7c3aed'},
                    {label:'Skipped', value:uploadResult.skipped, color:'#d97706'},
                  ].map(c=>(
                    <div key={c.label} style={{ textAlign:'center', padding:'10px', background:'white', borderRadius:'8px', boxShadow:'0 1px 3px rgba(0,0,0,0.1)' }}>
                      <div style={{ fontSize:'22px', fontWeight:'800', color:c.color }}>{c.value}</div>
                      <div style={{ fontSize:'11px', color:'#64748b' }}>{c.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ color:'#dc2626', fontWeight:'600' }}>❌ {uploadResult.message}</div>
            )}
          </div>
        )}
        {deleteTermResult && (
          <div style={{ marginTop:'12px', padding:'12px', borderRadius:'8px', background: deleteTermResult.success ? '#f0fdf4' : '#fff0f0', border: '1px solid ' + (deleteTermResult.success ? '#86efac' : '#fca5a5') }}>
            {deleteTermResult.success
              ? <div style={{ color:'#166534', fontWeight:'700' }}>✅ Deleted {deleteTermResult.deleted} terminated users. Remaining: {deleteTermResult.remaining}</div>
              : <div style={{ color:'#dc2626' }}>❌ {deleteTermResult.message}</div>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
